/**
 * Explore's live chart keeps up: it ticks.
 *
 * After a plot from a source without history (Live) on a rolling preset
 * ("Last 1 h"), a loop runs at the cadence of the plotted signals' tables (the
 * Live API's `poll_seconds`). Each tick moves the window forward to now, then:
 *
 * - **Line and scatter append.** Only rows after the newest one on screen are
 *   asked for - from `OVERLAP_S` before it, because a reading can land in
 *   ClickHouse a few seconds after a later one - merged in, replacing that
 *   overlap, and what fell out of the window is trimmed. One hour at 10 s is
 *   then a request for a few rows, not 360 of each signal.
 * - **Everything else refetches the window**: the summary types (Number, Gauge,
 *   Pie, ...) are about the whole window, and derived signals, outlier removal
 *   and fits need the history the window holds. At live window sizes that is
 *   cheap. Every `FULL_EVERY` ticks a line chart refetches too, so anything an
 *   append got wrong heals itself.
 *
 * A tick never undoes the viewer: it waits while the chart is zoomed, hovered,
 * or the browser tab is hidden, and the chip in the chart header pauses and
 * resumes it (`docs/plans/data-sources.md`, "Live charts").
 */

import { Atom } from '../../state/atom.ts';
import { runPlot } from '../../api/endpoints.ts';
import type { AnyPlotResponse, PlotResponse } from '../../api/types.ts';
import { doPlot } from './plot-actions.ts';
import { isXY } from '../../api/plot-shapes.ts';
import { activePreset, allColumns, endDate, plottedShipIds, startDate } from '../../state/store.ts';
import { plotState, type LiveMotion } from '../../state/plot-state.ts';
import { periodComparison } from '../../state/period-comparison.ts';
import { sourceHasHistory } from '../../state/data-sources.ts';
import { ROLLING_PRESET_MINUTES, fmtUtcDatetime } from '../../utils/dates.ts';
import { applyDatePreset } from '../ship/date-presets.ts';
import { buildPlotRequest, plotRequestSignature } from './plot-request.ts';
import { fetchShipPlots, mergeOverlay, type ShipPlotResult } from './multi-ship-plot.ts';
import { lastPlotData, plotXWindow, plottedSignature, plottedSource } from './plot-ui-state.ts';
import { computeXExtent, runtime } from './plot-runtime.ts';
import { renderChart } from './render/render-chart.ts';
import { updateXWindowReadout } from './zoom.ts';

/** Seconds re-asked before the newest point, for readings that land late. */
export const OVERLAP_S = 30;
/** Every this many ticks an appending chart refetches its whole window. */
export const FULL_EVERY = 30;
/** Cadence when no plotted signal says its own. */
const DEFAULT_CADENCE_S = 10;
/** How often a held tick (zoomed, hovered, hidden) looks again. */
const RECHECK_MS = 1000;

export interface LiveTickState {
  /** Off: the chart on screen does not tick (not Live, or a fixed window). */
  running: boolean;
  /** Paused by the viewer from the chip. */
  paused: boolean;
  /** Seconds between ticks. */
  cadenceS: number;
}

export const liveTick = new Atom<LiveTickState>({ running: false, paused: false, cadenceS: DEFAULT_CADENCE_S });

let timer: number | undefined;
/** Ticks since the loop started; counts on across the refetches it triggers. */
let tickCount = 0;
let hovering = false;
let inFlight = false;
/** The chart's recipe at plot time, window aside - see `recipeNow`. */
let recipe: string | null = null;

/**
 * What the sidebar would plot now, without its window. A tick only moves the
 * window: if the ship, signals or settings changed since the chart was drawn,
 * the chart waits for Plot rather than quietly becoming something else.
 */
function recipeNow(): string {
  const body = { ...buildPlotRequest().body } as unknown as Record<string, unknown>;
  delete body.start_date;
  delete body.end_date;
  return JSON.stringify([body, plottedShipIds()]);
}

/* ── The merge (pure) ─────────────────────────────────────── */

/**
 * `prev` with `delta` appended: `prev`'s points from `replaceFrom` on are
 * replaced by `delta`'s, and everything before `windowStart` is dropped.
 * Series are matched by name and ship; a series only one side has is padded
 * with nulls on the other. Arrays aligned with `x` (series values, colour and
 * slicer values) are carried the same way.
 */
export function appendXY(
  prev: PlotResponse,
  delta: PlotResponse,
  replaceFrom: number,
  windowStart: number,
): PlotResponse {
  const keep: number[] = [];
  prev.x.forEach((x, i) => {
    if (x >= windowStart && x < replaceFrom) keep.push(i);
  });
  const add: number[] = [];
  delta.x.forEach((x, i) => {
    if (x >= windowStart) add.push(i);
  });
  const nulls = (n: number): Array<null> => new Array<null>(n).fill(null);
  const pick = <T>(arr: ArrayLike<T> | null | undefined, idx: number[]): Array<T | null> =>
    arr ? idx.map((i) => arr[i] ?? null) : nulls(idx.length);

  const key = (s: { name: string; ship?: string | null }): string => `${s.ship ?? ''}\u0000${s.name}`;
  const deltaByKey = new Map(delta.series.map((s) => [key(s), s]));
  const seen = new Set<string>();
  const series = prev.series.map((s) => {
    seen.add(key(s));
    const d = deltaByKey.get(key(s));
    return { ...s, values: [...pick(s.values, keep), ...pick(d?.values, add)] };
  });
  for (const d of delta.series) {
    if (!seen.has(key(d))) series.push({ ...d, values: [...nulls(keep.length), ...pick(d.values, add)] });
  }

  const x = [...keep.map((i) => prev.x[i]!), ...add.map((i) => delta.x[i]!)];
  const colorValues =
    prev.color_values || delta.color_values
      ? [...pick(prev.color_values, keep), ...pick(delta.color_values, add)]
      : null;
  const sliceKeys = new Set([...Object.keys(prev.slice_values ?? {}), ...Object.keys(delta.slice_values ?? {})]);
  const sliceValues: Record<string, Array<number | null>> = {};
  for (const k of sliceKeys) {
    sliceValues[k] = [...pick(prev.slice_values?.[k], keep), ...pick(delta.slice_values?.[k], add)];
  }

  // The first draw's warning (a downsampling note, say) is about a window that
  // has moved on; a tick's own empty-window note is not news either.
  const { warning: _warning, ...rest } = prev;
  return {
    ...rest,
    x,
    series,
    color_values: colorValues,
    slice_values: sliceValues,
    first_x: x[0] ?? null,
    last_x: x.length ? x[x.length - 1]! : null,
    returned_points: x.length,
    source_rows: keep.length + add.length,
  };
}

/* ── Following the clock ──────────────────────────────────── */

/*
 * Between ticks the axis slides with the clock, like a live panel: every
 * animation frame the x scale is set to the window back from now (and, for the
 * `now` layout, as far ahead). While following, that window *is* the chart's
 * data range (`runtime.dataRange`), so Explore's zoom logic needs no special
 * case: the moving view reads as not zoomed, a drag reads as a zoom - which
 * holds the slide and the ticks - and Reset zoom lands back on now. How it
 * moves is the chart's ⚙ **Motion** (`style.live_motion`): every frame, once a
 * second, or only when a tick lands.
 */

let raf = 0;
let lastStep = 0;

function motion(): LiveMotion {
  return plotState.value.style.live_motion;
}

/** The window the axis shows at `nowS`, or null off a rolling preset. */
export function clockWindow(nowS: number): { min: number; max: number } | null {
  const minutes = ROLLING_PRESET_MINUTES[activePreset.value];
  if (minutes === undefined) return null;
  const span = minutes * 60;
  const ahead = plotState.value.style.live_layout === 'now' ? span : 0;
  return { min: nowS - span, max: nowS + ahead };
}

/** One step: move the axis to now, unless the viewer is holding the chart. */
export function followClock(): void {
  const data = runtime.lastData;
  if (!liveTick.value.running || !data || !isXY(data) || !data.x_is_time) return;
  if (liveTick.value.paused || hovering || plotXWindow.value?.isZoomed) return;
  if (typeof document !== 'undefined' && document.hidden) return;
  const nowS = motion() === 'smooth' ? Date.now() / 1000 : Math.floor(Date.now() / 1000);
  const win = clockWindow(nowS);
  if (!win) return;
  runtime.liveNowS = nowS;
  runtime.dataRange = win;
  const targets = runtime.gridInstances.length ? runtime.gridInstances : runtime.instance ? [runtime.instance] : [];
  for (const u of targets) u.setScale('x', win);
}

function followLoop(ts: number): void {
  raf = requestAnimationFrame(followLoop);
  // Off: the axis moves when a tick lands (`show` follows once), not between.
  if (motion() === 'off') return;
  if (motion() === 'steps' && ts - lastStep < 1000) return;
  lastStep = ts;
  followClock();
}

function startFollowing(): void {
  if (!raf && typeof requestAnimationFrame === 'function') raf = requestAnimationFrame(followLoop);
  followClock();
}

function stopFollowing(): void {
  if (raf) cancelAnimationFrame(raf);
  raf = 0;
  runtime.liveNowS = null;
}

/* ── When to tick ─────────────────────────────────────────── */

/** Seconds between ticks: the fastest table among the plotted signals. */
function cadenceFor(columns: string[]): number {
  const byName = new Map(allColumns.value.map((c) => [c.name, c.cadence_s]));
  const known = columns.map((c) => byName.get(c)).filter((s): s is number => !!s && s > 0);
  return known.length ? Math.min(...known) : DEFAULT_CADENCE_S;
}

/** Whether the chart on screen should tick at all. */
function shouldRun(): boolean {
  return (
    !sourceHasHistory(plottedSource.value) &&
    ROLLING_PRESET_MINUTES[activePreset.value] !== undefined &&
    !periodComparison.value &&
    !!lastPlotData.value
  );
}

/** Why a tick has to wait, or null when it can run. */
function heldBy(): string | null {
  if (liveTick.value.paused) return 'paused';
  if (typeof document !== 'undefined' && document.hidden) return 'hidden';
  if (plotXWindow.value?.isZoomed) return 'zoomed';
  if (hovering) return 'hovering';
  if (runtime.isFetching || inFlight) return 'busy';
  if (recipe !== null && recipeNow() !== recipe) return 'changed';
  return null;
}

/**
 * Start or stop the loop for the chart now on screen. Called after every plot;
 * a plot that is not Live, or not on a rolling window, stops it.
 */
export function syncLiveTicking(): void {
  window.clearTimeout(timer);
  timer = undefined;
  if (!shouldRun()) {
    stopFollowing();
    if (liveTick.value.running) liveTick.set({ ...liveTick.value, running: false });
    return;
  }
  const s = plotState.value;
  const cadenceS = cadenceFor([...s.y_left, ...s.y_right, s.x_column].filter(Boolean));
  recipe = recipeNow();
  liveTick.set({ running: true, paused: liveTick.value.paused, cadenceS });
  schedule(cadenceS * 1000);
  startFollowing();
}

export function stopLiveTicking(): void {
  window.clearTimeout(timer);
  timer = undefined;
  stopFollowing();
  if (liveTick.value.running) liveTick.set({ ...liveTick.value, running: false });
}

/** The chip's click: pause or resume. Resuming ticks at once. */
export function toggleLivePause(): void {
  const paused = !liveTick.value.paused;
  liveTick.set({ ...liveTick.value, paused });
  if (!paused && liveTick.value.running) {
    window.clearTimeout(timer);
    schedule(0);
  }
}

/** The chart reports the pointer over it: a tick waits until it leaves. */
export function setLiveHover(on: boolean): void {
  hovering = on;
}

function schedule(ms: number): void {
  timer = window.setTimeout(() => void tick(), ms);
}

async function tick(): Promise<void> {
  if (!liveTick.value.running) return;
  if (!shouldRun()) {
    stopLiveTicking();
    return;
  }
  if (heldBy()) {
    schedule(RECHECK_MS);
    return;
  }
  inFlight = true;
  let replotted = false;
  try {
    // The window rolls: "Last 1 h" is the hour before now, every tick.
    applyDatePreset(activePreset.value);
    tickCount++;
    const appended = appendable() && tickCount % FULL_EVERY !== 0 ? await appendTick() : false;
    if (!appended) {
      // The whole window, through the very path a Plot press takes - every
      // plot type and ship layout merges there - which restarts this loop.
      inFlight = false;
      replotted = true;
      await doPlot();
    }
  } catch {
    // A failed tick changes nothing on screen; the next one tries again. The
    // chip's age is what tells the viewer data has stopped arriving.
  } finally {
    inFlight = false;
    if (!replotted && liveTick.value.running) schedule(liveTick.value.cadenceS * 1000);
  }
}

/** Line and scatter with nothing that needs the window's history. */
function appendable(): boolean {
  const data = runtime.lastData;
  if (!data || !isXY(data) || !data.x_is_time) return false;
  const body = buildPlotRequest().body as unknown as Record<string, unknown>;
  const has = (k: string): boolean => Array.isArray(body[k]) && (body[k] as unknown[]).length > 0;
  return !has('fits') && !has('derived') && !body.outliers;
}

/** Ask for the rows since the newest on screen, and merge them in. */
async function appendTick(): Promise<boolean> {
  const prev = runtime.lastData;
  if (!prev || !isXY(prev) || prev.last_x == null) return false;
  const windowStart = Date.parse(`${startDate.value}Z`) / 1000;
  const from = Math.max(windowStart, prev.last_x - OVERLAP_S);
  const range = { start: fmtUtcDatetime(new Date(from * 1000)), end: endDate.value };

  let merged: PlotResponse | null;
  const byShip = runtime.lastByShip;
  if (byShip) {
    const deltas = await fetchShipPlots(plottedShipIds(), range, true);
    const next: ShipPlotResult[] = byShip.map((old) => {
      const d = deltas.find((r) => r.ship === old.ship)?.data;
      if (!old.data || !isXY(old.data) || !d || !isXY(d)) return old;
      return { ship: old.ship, data: appendXY(old.data, d, from, windowStart) };
    });
    runtime.lastByShip = next;
    merged = mergeOverlay(next);
  } else {
    const d = await runPlot(buildPlotRequest(range).body, true);
    if (!isXY(d)) return false;
    merged = appendXY(prev, d, from, windowStart);
  }
  if (!merged) return false;
  show(merged);
  return true;
}

function show(data: AnyPlotResponse): void {
  runtime.lastData = data;
  lastPlotData.set(data);
  // While following, the chart's range is the clock's window, not the data's:
  // the rebuild below is born on it.
  if (runtime.liveNowS == null) runtime.dataRange = computeXExtent(data);
  // The request now on screen is the moved window's, so Plot stays disabled.
  plottedSignature.set(plotRequestSignature(buildPlotRequest().body, plottedShipIds()));
  renderChart(data);
  if (runtime.dataRange && isXY(data)) {
    updateXWindowReadout(runtime.dataRange.min, runtime.dataRange.max, data.x_is_time, false);
  }
  // Before the new chart's first paint, so a tick never flashes the data's
  // own extent between two frames of the clock's.
  followClock();
}
